import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal } from '@angular/core';
import { IonCol, IonContent, IonGrid, IonRow } from '@ionic/angular/standalone';
import { SignalSettingsProvider, maxSignalDashLength, signalColors, signalDashes } from '../../providers/signal-settings-provider';
import { LineSampleComponent } from '../line-sample.component';

@Component({
    selector: 'app-signal-style-popover',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonContent, IonGrid, IonRow, IonCol, LineSampleComponent],
    template: `
        <ion-content class="ion-padding" scrollY="true">
            <ion-grid>
                <ion-row>
                    <ion-col>
                        <div class="style-label" style="font-weight: bold; margin-bottom: 8px;">Color</div>
                        <div class="style-selector" style="display: flex; flex-direction: column; gap: 4px;">
                            @for (color of colors; track $index; let index = $index) {
                                <div
                                    class="color-btn"
                                    [style.border]="
                                        colorIndex() == index
                                            ? '2px solid var(--ion-color-step-800)'
                                            : '1px solid var(--ion-color-step-400)'
                                    "
                                    [style.padding]="colorIndex() == index ? '3px' : '4px'"
                                    style="border-radius: 4px; cursor: pointer; display: flex; align-items: center;"
                                    (click)="setColor(index)"
                                >
                                    <line-sample [color]="color" [dash]="dashes[dashIndex()]" [width]="sampleWidth"></line-sample>
                                </div>
                            }
                        </div>
                    </ion-col>

                    <ion-col>
                        <div class="style-label" style="font-weight: bold; margin-bottom: 8px;">Dash</div>
                        <div class="style-selector" style="display: flex; flex-direction: column; gap: 4px;">
                            @for (dash of dashes; track $index; let index = $index) {
                                <div
                                    class="dash-btn"
                                    [style.border]="
                                        dashIndex() == index ? '2px solid var(--ion-color-step-800)' : '1px solid var(--ion-color-step-400)'
                                    "
                                    [style.padding]="dashIndex() == index ? '3px' : '4px'"
                                    style="border-radius: 4px; cursor: pointer; display: flex; align-items: center;"
                                    (click)="setDash(index)"
                                >
                                    <line-sample [color]="colors[colorIndex()]" [dash]="dash" [width]="sampleWidth"></line-sample>
                                </div>
                            }
                        </div>
                    </ion-col>
                </ion-row>
            </ion-grid>
        </ion-content>
    `,
    styles: `
        .style-label {
            font-size: 0.8em;
            opacity: 0.8;
            margin-left: 2px;
        }
    `,
})
export class SignalStylePopoverComponent implements OnInit {
    readonly signalKey = input.required<string>();

    readonly colorIndex = signal(0);
    readonly dashIndex = signal(0);

    readonly colors = signalColors;
    readonly dashes = signalDashes;
    readonly sampleWidth = maxSignalDashLength;

    private readonly signalSettingsProvider = inject(SignalSettingsProvider);

    ngOnInit() {
        const style = this.signalSettingsProvider.computedStyles[this.signalKey()];
        this.colorIndex.set(style?.color ?? 0);
        this.dashIndex.set(style?.dash ?? 0);
    }

    setColor(index: number) {
        this.colorIndex.set(index);
        this.commitStyle();
    }

    setDash(index: number) {
        this.dashIndex.set(index);
        this.commitStyle();
    }

    private commitStyle() {
        this.signalSettingsProvider.setStyle(this.signalKey(), { color: this.colorIndex(), dash: this.dashIndex() });
    }
}
